import { useResponsiveProp, useTheme } from '@shopify/restyle';
import ImageViewerModal from 'app/design/primitives/image-viewer-modal';

import { useImagePickerCamera } from 'app/hooks/use-imagepicker-camera';
import { getImageVariantUrl } from 'app/lib/image-variants';
import { Placeholder, PlaceholderLine, ShineOverlay } from 'app/overrides/rn-placeholder';
import type { ImagePickerAsset, ImagePickerSuccessResult } from 'expo-image-picker';
import { useState } from 'react';
import { Dimensions, Platform, Pressable } from 'react-native';
import { Column, Row } from 'app/design/primitives/box';
import { CorpoImage } from './corp-image';
import { OpenSettingsPrompt } from 'app/components/open-settings-prompt';
import { Text } from 'app/design/primitives/text';
import type { Listing } from 'app/types/listing';
import { ListingSubtype } from 'app/types/listing';
import { ImageSwiper, type SwiperImage } from 'app/design/primitives/image-swiper';
import { Button } from 'app/design/primitives/button';
import { Modal } from 'app/design/primitives/modal';
import { MultiImagePicker } from 'app/design/primitives/multi-image-picker';
import { useTranslation } from 'react-i18next';
import { ShareApple } from 'app/design/icons/shareApple';
import { ShareAndroid } from 'app/design/icons/shareAndroid';
import type { Theme } from 'app/shopify';
import { IconCamera } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

interface MultiPickerModalProps {
  open: boolean;
  onClose: () => void;
  images: ImagePickerAsset[];
  onChange: (images: ImagePickerAsset[]) => void;
  onSave: () => void;
}

function MultiPickerModal({ open, onClose, images, onChange, onSave }: MultiPickerModalProps) {
  const { t } = useTranslation('image-picker');

  return (
    <Modal onClose={onClose} open={open} transparent>
      <Modal.Card gap="spacing.general.x4">
        <Modal.Title allowClose onClose={onClose}>
          {t('manage')}
        </Modal.Title>
        <MultiImagePicker onChange={onChange} title={t('add')} images={images} withInlinePicker />
        <Button variant="primary" onPress={onSave}>
          <Text color="white" fontWeight="500">
            {t('save')}
          </Text>
        </Button>
      </Modal.Card>
    </Modal>
  );
}

interface ListingHeroProps extends Pick<Listing, 'categoryPrimary' | 'pictures' | 'type' | 'subtype' | 'userId'> {
  groups?: { picture?: string }[];
  hideCorpoBadge?: boolean;
  onChangePictures?: (results: ImagePickerSuccessResult, deletedIds: string[]) => void;
  onSharePress?: () => Promise<void>;
  lender?: Listing['lender'];
  allPicturesProp?: SwiperImage[];
  borderRadius?: number;
  height?: number;
  paginationBottom?: number;
  paginationTop?: number;
  onImageIndexChange?: (index: number) => void;
}

const WIDTH = Dimensions.get('window').width;
const PAGE_WIDTH = WIDTH * 0.9;

function ListingShareButton({ onSharePress }: Pick<ListingHeroProps, 'onSharePress'>) {
  const theme = useTheme<Theme>();
  if (!onSharePress) return null;
  return (
    <Pressable
      onPress={() => void onSharePress()}
      style={{
        position: 'absolute',
        bottom: theme.spacing['spacing.general.x2'],
        right: theme.spacing['spacing.general.x2'],
        backgroundColor: theme.colors['surface.tertiary-neutral'],
        borderRadius: 8,
        paddingRight: Platform.OS === 'android' ? 2.5 : 0,
        paddingBottom: Platform.OS === 'android' ? 1.5 : 0,
      }}
    >
      {Platform.OS === 'ios' ? <ShareApple /> : <ShareAndroid />}
    </Pressable>
  );
}

function ListingHeroMedia({
  allPictures,
  categoryPrimary,
  height,
  isDefaultRequestIcon,
  lender,
  onImageIndexChange,
  onImagePress,
  onSharePress,
  paginationBottom,
  paginationTop,
  subtype,
}: Pick<
  ListingHeroProps,
  | 'categoryPrimary'
  | 'height'
  | 'lender'
  | 'onImageIndexChange'
  | 'onSharePress'
  | 'paginationBottom'
  | 'paginationTop'
  | 'subtype'
> & {
  allPictures: SwiperImage[];
  isDefaultRequestIcon: boolean | undefined;
  onImagePress: (index: number) => void;
}) {
  const theme = useTheme<Theme>();
  const shareButton = <ListingShareButton onSharePress={onSharePress} />;
  if (isDefaultRequestIcon) {
    return (
      <Row
        alignItems="center"
        style={{
          height,
          backgroundColor:
            subtype === ListingSubtype.service
              ? theme.colors['surface.brand.secondary-2.subtle']
              : theme.colors['surface.brand.secondary-5.subtle'],
        }}
      >
        <Text style={{ flexGrow: 1, textAlign: 'center', fontSize: 100 }}>
          {subtype === ListingSubtype.service ? '🙏' : lender ? '🤝' : '👋'}
        </Text>
        {shareButton}
      </Row>
    );
  }

  return (
    <ImageSwiper
      backgroundColor={categoryPrimary.colorLight || 'transparent'}
      height={height}
      images={allPictures}
      onActiveIndexChange={onImageIndexChange}
      onPress={onImagePress}
      paginationBottom={paginationBottom}
      paginationTop={paginationTop}
      shareIcon={shareButton}
    />
  );
}

function ListingHeroBadges({
  groups,
  hideCorpoBadge,
  onChangePictures,
  onEditPictures,
  onSharePress,
}: Pick<ListingHeroProps, 'groups' | 'hideCorpoBadge' | 'onChangePictures' | 'onSharePress'> & {
  onEditPictures: () => void;
}) {
  const theme = useTheme<Theme>();
  return (
    <>
      {groups?.length && !hideCorpoBadge ? (
        <CorpoImage
          picture={groups[0]?.picture}
          style={{ position: 'absolute', bottom: onSharePress ? 52 : 6, right: 6, zIndex: 999 }}
        />
      ) : null}
      {onChangePictures ? (
        <Pressable
          onPress={onEditPictures}
          style={{
            position: 'absolute',
            bottom: 12,
            left: 9,
            backgroundColor: theme.colors['surface.tertiary-neutral'],
            borderRadius: 8,
            padding: 1,
          }}
        >
          <IconCamera color={theme.colors['surface.inverse.default']} size={30} strokeWidth={ICON_STROKE_WIDTH} />
        </Pressable>
      ) : null}
    </>
  );
}

export function ListingHero({
  pictures,
  groups,
  hideCorpoBadge,
  categoryPrimary,
  subtype,
  onChangePictures,
  onSharePress,
  lender,
  allPicturesProp,
  borderRadius,
  height = 174,
  paginationBottom,
  paginationTop,
  onImageIndexChange,
}: ListingHeroProps) {
  const [isImageViewerModalOpen, setIsImageViewerModalOpen] = useState(false);
  const [viewerStartIndex, setViewerStartIndex] = useState(0);
  const [isMultiPickerOpen, setIsMultiPickerOpen] = useState(false);
  const [pickerImages, setPickerImages] = useState<ImagePickerAsset[]>([]);
  const theme = useTheme<Theme>();
  const allPictures: SwiperImage[] =
    allPicturesProp ??
    (pictures ?? []).flatMap((picture) => (picture.url ? [{ id: picture.id, url: picture.url }] : []));
  const isDefaultRequestIcon = allPictures[0]?.url.includes('/request-icon.svg');
  const isSVG = allPictures[0]?.url.includes('.svg');
  const canZoomOnPhoto = !isSVG;

  const { settingsPromptState } = useImagePickerCamera();

  const imageViewerUrls = allPictures.map((p) => ({
    url: getImageVariantUrl(p.url, 'medium') ?? p.url,
  }));

  const handleImagePress = (index: number) => {
    if (canZoomOnPhoto) {
      setViewerStartIndex(index);
      setIsImageViewerModalOpen(true);
    }
  };

  const handleOpenMultiPicker = () => {
    setPickerImages(allPictures.map((p) => ({ uri: p.url, width: 0, height: 0, assetId: p.id })));
    setIsMultiPickerOpen(true);
  };

  const handleSaveMultiPicker = () => {
    const existingUris = new Set(allPictures.map((p) => p.url));
    const added = pickerImages.filter((asset) => !existingUris.has(asset.uri));
    const newUris = new Set(pickerImages.map((asset) => asset.uri));
    const deletedIds = allPictures.flatMap((picture) => (picture.id && !newUris.has(picture.url) ? [picture.id] : []));

    if (added.length === 0 && deletedIds.length === 0) {
      setIsMultiPickerOpen(false);
      return;
    }

    onChangePictures?.({ canceled: false, assets: added }, deletedIds);
    setIsMultiPickerOpen(false);
  };

  return (
    <>
      <Column
        style={{
          width: '100%',
          height,
          overflow: 'hidden',
          borderRadius: borderRadius ?? theme.borderRadii['radius.lg'],
        }}
      >
        <ListingHeroMedia
          allPictures={allPictures}
          categoryPrimary={categoryPrimary}
          height={height}
          isDefaultRequestIcon={isDefaultRequestIcon}
          lender={lender}
          onImageIndexChange={onImageIndexChange}
          onImagePress={handleImagePress}
          onSharePress={onSharePress}
          paginationBottom={paginationBottom}
          paginationTop={paginationTop}
          subtype={subtype}
        />

        <ImageViewerModal
          imageUrls={imageViewerUrls.length > 0 ? imageViewerUrls : [{ url: '' }]}
          index={viewerStartIndex}
          onClose={() => setIsImageViewerModalOpen(false)}
          open={isImageViewerModalOpen}
        />
        <ListingHeroBadges
          groups={groups}
          hideCorpoBadge={hideCorpoBadge}
          onChangePictures={onChangePictures}
          onEditPictures={handleOpenMultiPicker}
          onSharePress={onSharePress}
        />
      </Column>
      <MultiPickerModal
        open={isMultiPickerOpen}
        onClose={() => setIsMultiPickerOpen(false)}
        images={pickerImages}
        onChange={setPickerImages}
        onSave={handleSaveMultiPicker}
      />
      <OpenSettingsPrompt
        isOpen={settingsPromptState.isOpen}
        onClose={settingsPromptState.onClose}
        settingName="camera"
      />
    </>
  );
}

ListingHero.Skeleton = function ListingHeroSkeleton() {
  const resolvedSize = useResponsiveProp(PAGE_WIDTH);

  return (
    <Placeholder Animation={ShineOverlay}>
      <PlaceholderLine
        style={{
          width: resolvedSize,
          height: resolvedSize,
          aspectRatio: 1,
        }}
      />
    </Placeholder>
  );
};
